import type { Store } from '../types/location.types';
import { genderFromApi } from '../utils/petGender';
import type { Category, ServiceCategory } from '../types/service.types';
import { API_CONFIG, CMS_BASE_URL, API_ENVIRONMENT, optimizedApiService } from '../config/api.config';
import { getCMSJWT } from './auth.service';
import { SESSION_ENDED_EVENT } from './sessionEvents';
import { getBreedById } from './breed.service';
import { behaviourName } from '../utils/petBehaviour';
import { precisionFromApi } from '../utils/petForm';

// Interface for the new /api/current response
interface ApiCurrentUserResponse {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  picture: string;
  createdDate: string;
  modifiedDate: string;
  lastAppointmentDate: string | null;
  /** Days after the last visit they're due, by the salon's rebook reminder; null while Business Manager is off (BM 08). */
  rebookIntervalDays?: number | null;
  totalSales: number;
  isBlocked: boolean;
  blockedReason: number;
  lastBlockedDate: string;
  lastUnblockedDate: string;
  mobileNumber: {
    number: string;
  };
  pets: ApiPet[];
}

interface ApiPet {
  id: number;
  name: string;
  petType: number; // 1 = cat, 2 = dog
  petGender: number | null; // Male 0, Female 1 (2 was this site's male, utils/petGender)
  dateOfBirth: string | null;
  /** "day", "month" or "none": how much of the birthday is known (mpgadmin D102). */
  birthdayPrecision?: string | null;
  breed: {
    id: number;
    name: string;
    petType: number;
  };
  tagIds: number[];
  tags: any[];
  breedId: number;
  weight: number;
  petAggressiveBehavior: string | number | null;
  note: string | null;
  clientId: number;
}

// App's Pet interface (keeping existing for compatibility)
export interface Pet {
  id?: number;
  type?: 'dog' | 'cat';
  name?: string;
  breed?: string;
  breedId?: number;
  gender?: string;
  weight?: string;
  age?: number;
  dob?: string;
  /** "month" when only the month is known (a rescue's, or an age): the pet form opens on the month. */
  birthdayPrecision?: 'day' | 'month' | 'none';
  aggression?: string;
  info?: string;
}

export const fetchStores = async (forceRefresh = false): Promise<Store[]> => {
  // Use the cached store service to minimize API calls
  const { storeService } = await import('./store.service');
  return storeService.getStores(forceRefresh);
};

export const fetchCategories = async (): Promise<Category[]> => {
  try {
    const url = `${CMS_BASE_URL}/api/categories?environment=${API_ENVIRONMENT}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: API_CONFIG.headers
    });

    if (!response.ok) {
      throw new Error('Failed to fetch categories');
    }
    
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching categories:', error);
    throw error;
  }
};

export const fetchServiceCategories = async (storeId: string): Promise<ServiceCategory[]> => {
  try {
    // Use cache for cat services (type 1)
    const data = await serviceCategoriesCache.getServiceCategories(storeId, 1);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error fetching service categories:', error);
    throw error;
  }
}; 

// Service Categories Cache
interface ServiceCacheEntry {
  data: any[];
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

class ServiceCategoriesCache {
  private cache: Map<string, ServiceCacheEntry> = new Map();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

  private getCacheKey(storeId: string, serviceType: number): string {
    return `${storeId}-${serviceType}`;
  }

  async getServiceCategories(storeId: string, serviceType: number): Promise<any[]> {
    const cacheKey = this.getCacheKey(storeId, serviceType);
    const cached = this.cache.get(cacheKey);
    
    // Check if cache is valid
    if (cached && (Date.now() - cached.timestamp) < cached.ttl) {
      return cached.data;
    }

    // Fetch fresh data
    try {
      const response = await fetch(`${CMS_BASE_URL}/api/grooming-service-categories/by-filter?storeId=${storeId}&groomingServiceTypeDto=${serviceType}&environment=${API_ENVIRONMENT}`, {
        method: 'GET',
        headers: API_CONFIG.headers
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch service categories: ${response.status}`);
      }

      const serviceData = await response.json();
      
      // Cache the result
      this.cache.set(cacheKey, {
        data: serviceData,
        timestamp: Date.now(),
        ttl: this.DEFAULT_TTL
      });

      return serviceData;
    } catch (error) {
      console.error(`Error fetching service categories for store ${storeId}, type ${serviceType}:`, error);
      
      // Return cached data if available, even if expired
      if (cached) {
        return cached.data;
      }
      
      throw error;
    }
  }

  // Method to clear cache (useful for testing or manual refresh)
  clearCache(): void {
    this.cache.clear();
  }

  // Method to clear specific cache entry
  clearCacheForStore(storeId: string, serviceType?: number): void {
    if (serviceType !== undefined) {
      const cacheKey = this.getCacheKey(storeId, serviceType);
      this.cache.delete(cacheKey);
    } else {
      // Clear all cache entries for this store
      const keysToDelete = Array.from(this.cache.keys()).filter(key => key.startsWith(`${storeId}-`));
      keysToDelete.forEach(key => this.cache.delete(key));
    }
  }
}

// Export singleton instance
export const serviceCategoriesCache = new ServiceCategoriesCache();

/**
 * Forget a location's menus, both of the page's copies (the services step's
 * and the one Time & groomer and the bookings read), for each pet type the
 * booking had (G30): the API refused a line as not on the menu, or not the
 * business's (mpgadmin G119, G120), so the next read must be the salon's
 * menu as it is now. The
 * refusal doesn't say which pet's line it was; with no types, both.
 */
export const forgetMenus = (storeId: number | string | null | undefined, types: Array<1 | 2> = [1, 2]): void => {
  const id = Number(storeId);
  if (!Number.isInteger(id) || id <= 0) return;
  Array.from(new Set(types)).forEach(type => {
    optimizedApiService.forgetDetailedGroomingServices(id, type);
    serviceCategoriesCache.clearCacheForStore(String(id), type);
  });
};

// ===============================
// CURRENT USER & PETS API
// ===============================

// Helper function to calculate age from date of birth
const calculateAge = (dateOfBirth: string | null): number | undefined => {
  if (!dateOfBirth) return undefined;
  
  const birthDate = new Date(dateOfBirth);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    return age - 1;
  }
  
  return age;
};

// Transform API pet to app Pet format
const transformApiPetToAppPet = (apiPet: ApiPet): Pet => {
  // Map petType: 1 = cat, 2 = dog
  const petType = apiPet.petType === 1 ? 'cat' : apiPet.petType === 2 ? 'dog' : 'dog';
  
  // The sex as the API numbers it (H2, utils/petGender).
  const gender = genderFromApi(apiPet.petGender);
  
  // Calculate age from date of birth
  const age = calculateAge(apiPet.dateOfBirth);
  
  // Format weight - convert number to string with unit
  const weight = apiPet.weight > 0 ? `${apiPet.weight}kg` : '';
  
  return {
    id: apiPet.id,
    type: petType,
    name: apiPet.name,
    breed: apiPet.breed?.name || '',
    breedId: apiPet.breedId,
    gender: gender,
    weight: weight,
    age: age,
    dob: apiPet.dateOfBirth || '',
    birthdayPrecision: precisionFromApi(apiPet.birthdayPrecision),
    aggression: behaviourName(apiPet.petAggressiveBehavior),
    info: apiPet.note || ''
  };
};

// Function to enhance pets with breed names when missing
const enhancePetsWithBreedNames = async (pets: Pet[]): Promise<Pet[]> => {
  const enhancedPets = await Promise.all(
    pets.map(async (pet) => {
      // If breed name is missing but breedId exists, resolve it
      if ((!pet.breed || pet.breed === '') && pet.breedId) {
        try {
          const breedInfo = await getBreedById(pet.breedId);
          if (breedInfo) {
            return { ...pet, breed: breedInfo.name };
          }
        } catch (error) {
          console.warn(`Failed to resolve breed name for breedId ${pet.breedId}:`, error);
        }
      }
      return pet;
    })
  );
  return enhancedPets;
};

/**
 * The read of /api/current under way, by token: callers at the same moment
 * share it. The home page read it twice at once (App's own read and the pet
 * store's), and the booking pages' user check a third time.
 */
let currentRead: { jwt: string; promise: Promise<{ user: any; pets: Pet[] }> } | null = null;

// Fetch current user and pets from /api/current
export const fetchCurrentUserAndPets = (): Promise<{ user: any; pets: Pet[] }> => {
  const jwt = getCMSJWT();
  if (jwt && currentRead?.jwt === jwt) return currentRead.promise;
  const promise = readCurrentUserAndPets().finally(() => {
    if (currentRead?.promise === promise) currentRead = null;
  });
  if (jwt) currentRead = { jwt, promise };
  return promise;
};

const readCurrentUserAndPets = async (): Promise<{ user: any; pets: Pet[] }> => {
  try {
    const jwt = getCMSJWT();
    if (!jwt) {
      throw new Error('No authentication token available');
    }

    const url = `${CMS_BASE_URL}/api/current`;
    
    // Create a timeout controller
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
    
    let response: Response;
    try {
      response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${jwt}`,
          'Content-Type': 'application/json'
        },
        mode: 'cors',
        cache: 'no-cache',
        credentials: 'omit',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId); // Clear timeout if request succeeds
    } catch (fetchError) {
      clearTimeout(timeoutId);
      if (fetchError instanceof Error && fetchError.name === 'AbortError') {
        throw new Error('Request timeout - API call took too long');
      }
      throw fetchError;
    }

    if (response.status === 401) {
      // The session is over (G10): the token expired, or the CMS says its
      // client is gone, because the record was merged into another or
      // removed (`session_ended`, CMS G11, G13), or it is another salon's
      // (`other_salon`, CMS G11). Sign out cleanly either way (G1). Only a
      // client that is gone is news to the person, so only that is
      // announced; a day-old token ending stays quiet, as before.
      const body = await response.json().catch(() => ({}));
      const { signOut } = await import('./signOut');
      signOut();
      if (body?.code === 'session_ended') window.dispatchEvent(new Event(SESSION_ENDED_EVENT));
      throw new Error('SESSION_EXPIRED');
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ API Error:', response.status, errorText);
      throw new Error(`Failed to fetch current user: ${response.status} - ${errorText}`);
    }
    
    const data: ApiCurrentUserResponse = await response.json();
    
    // Transform user data
    const user = {
      id: data.id,
      name: `${data.firstName} ${data.lastName}`,
      email: data.email,
      picture: data.picture,
      firstName: data.firstName,
      lastName: data.lastName,
      mobileNumber: data.mobileNumber?.number || '',
      createdDate: data.createdDate,
      modifiedDate: data.modifiedDate,
      // "Usually due around …" (BM 08, utils/dueAgain): the last visit and the reminder's interval.
      lastAppointmentDate: data.lastAppointmentDate ?? null,
      rebookIntervalDays: data.rebookIntervalDays ?? null
    };
    
    // Transform pets data
    const pets: Pet[] = data.pets?.map(transformApiPetToAppPet) || [];
    
    // Enhance pets with breed names if missing
    const enhancedPets = await enhancePetsWithBreedNames(pets);
    
    return { user, pets: enhancedPets };
    
  } catch (error) {
    console.error('❌ Error fetching current user and pets:', error);
    throw error;
  }
};

// Fetch only pets (for refreshing after updates)
export const fetchCurrentUserPets = async (): Promise<Pet[]> => {
  try {
    const { pets } = await fetchCurrentUserAndPets();
    return pets;
  } catch (error) {
    console.error('Error fetching current user pets:', error);
    throw error;
  }
}; 

// Update user's mobile number
export const updateUserMobileNumber = async (mobileNumber: string): Promise<any> => {
  // Import phone formatting utility
  const { formatAustralianPhoneToE164 } = await import('../utils/phone');

  // Format to E.164 for Twilio compatibility
  const formattedNumber = formatAustralianPhoneToE164(mobileNumber);
  if (!formattedNumber) {
    throw new Error('Invalid phone number format. Please enter a valid Australian mobile number.');
  }

  try {
    const jwt = getCMSJWT();
    if (!jwt) {
      throw new Error('No authentication token available');
    }

    // First fetch current user data to get all required fields
    const currentResponse = await fetch(`${CMS_BASE_URL}/api/current`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      },
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'omit'
    });

    if (!currentResponse.ok) {
      throw new Error(`Failed to fetch current user: ${currentResponse.status}`);
    }

    const currentData: ApiCurrentUserResponse = await currentResponse.json();

    // Prepare the update payload
    const updatePayload = {
      id: currentData.id,
      firstName: currentData.firstName,
      lastName: currentData.lastName,
      email: currentData.email,
      picture: currentData.picture || '',
      createdDate: currentData.createdDate,
      modifiedDate: new Date().toISOString(),
      isBlocked: currentData.isBlocked ?? false,
      blockedReason: currentData.blockedReason ?? 0,
      mobileNumber: formattedNumber
    };

    // Send the update request
    const updateResponse = await fetch(`${CMS_BASE_URL}/api/client`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${jwt}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(updatePayload)
    });

    if (!updateResponse.ok) {
      const errorText = await updateResponse.text();
      console.error('Mobile number update failed:', errorText);
      // The CMS uses the mobile number as a unique identity. A number already on another client
      // comes back as a Resource Conflict — but the CMS wraps it in a generic 500 ("An error
      // occurred while updating client") with the real conflict nested in the body, so we match
      // on the body shape (not just the status). Surface it as a typed error so the UI can offer
      // "call the shop" / "log in" instead of a dead-end "try again".
      const lower = (errorText || '').toLowerCase();
      const isTaken =
        updateResponse.status === 409 ||
        /resource conflict|already used by another|already registered|already in use|conflictclientid|duplicate/.test(lower);
      const err = new Error(
        isTaken
          ? 'This mobile number is already registered to another account.'
          : `Failed to update mobile number: ${updateResponse.status}`
      ) as Error & { code?: string; status?: number };
      err.code = isTaken ? 'MOBILE_TAKEN' : 'MOBILE_UPDATE_FAILED';
      err.status = updateResponse.status;
      throw err;
    }

    const updatedUser = await updateResponse.json();
    return updatedUser;
  } catch (error) {
    console.error('❌ Error updating mobile number:', error);
    throw error;
  }
}; 
/**
 * The client's usual pickup contact (SF 74d; mpgadmin G105, D67): `mobile`
 * "" removes it. The client update writes the client's own mobile, email and
 * photo from the body, so they go back exactly as `/api/current` gave them:
 * this never changes the client's sign-in number. The API checks the mobile
 * (an Australian mobile, stored +61…) and answers 400 `pickup-mobile` if not.
 */
export const updateUsualPickupContact = async (
  name: string,
  mobile: string
): Promise<{ pickupContactName: string | null; pickupContactMobile: string | null }> => {
  const jwt = getCMSJWT();
  if (!jwt) throw new Error('No authentication token available');
  const headers = { Authorization: `Bearer ${jwt}`, 'Content-Type': 'application/json' };

  const currentResponse = await fetch(`${CMS_BASE_URL}/api/current`, {
    method: 'GET',
    headers,
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'omit',
  });
  if (!currentResponse.ok) throw new Error(`Failed to fetch current user: ${currentResponse.status}`);
  const current: any = await currentResponse.json();

  const removing = !mobile.trim();
  const updateResponse = await fetch(`${CMS_BASE_URL}/api/client`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({
      id: current.id,
      firstName: current.firstName,
      lastName: current.lastName,
      email: current.email,
      picture: current.picture || '',
      createdDate: current.createdDate,
      modifiedDate: new Date().toISOString(),
      isBlocked: current.isBlocked ?? false,
      blockedReason: current.blockedReason ?? 0,
      // As it is: the usual contact is never the client's own number (G105).
      mobileNumber: current.mobileNumber ?? null,
      pickupContactName: removing ? '' : name.trim(),
      pickupContactMobile: removing ? '' : mobile.trim(),
    }),
  });
  if (!updateResponse.ok) {
    const text = await updateResponse.text();
    const { pickupMobileRefusal, PickupMobileError } = await import('../utils/pickupContact');
    const refused = pickupMobileRefusal(text);
    if (refused) throw new PickupMobileError(refused);
    throw new Error(`Failed to save the pickup contact: ${updateResponse.status}`);
  }
  const saved: any = await updateResponse.json();
  return { pickupContactName: saved?.pickupContactName ?? null, pickupContactMobile: saved?.pickupContactMobile ?? null };
};
